import React, { useState, useEffect, useCallback } from 'react'
import './GisLightningAnalysis.css'

// Importing header
import TeamHeader from '../TeamHeader/TeamHeader'

// Filter
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";
import DateFilter, { DateRange } from '../TeamPortalUtil/Date Filter/DateFilter';

// Chart Imports
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

// Loading import
import LoadingSpinner from '../../backend/components/LoadingSpinner';

// Leaflet imports
import {
    MapContainer,
    TileLayer,
    ScaleControl,
    useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import HeatmapOverlay from "leaflet-heatmap";

// Icons
import { BiPieChartAlt2 } from 'react-icons/bi'
import { BsList } from 'react-icons/bs'

// Interfaces
type LightningStatus = 'Yes' | 'No' | 'Maybe'

type HrlBucket =
    | 'None'
    | '0'
    | '10'
    | '20'
    | '30'
    | '40'
    | '50'
    | '60'
    | '70'
    | '80'
    | '90+'

interface NotionChildDatabase {
    id: string
    title: string
}

interface GisFire {
    id: string
    fid: number
    fire_name: string
    lightning_5day: LightningStatus | null
    hrl_rating: number | null
}

interface GisArchiveLightning {
    lightning_group_id: number;
    strikes: Record<string, number>;  // polygon string → strike count
    counties: Record<string, number>;
    date: string; // ISO string
    region: string;
}

/* -----------------------------------------------------------------------
   HeatMapLayer — renders a leaflet-heatmap overlay inside a MapContainer
----------------------------------------------------------------------- */
interface HeatPoint {
    lat: number;
    lng: number;
    count: number;
}

interface HeatMapLayerProps {
    data: HeatPoint[];
    onExtremaChange?: (min: number, max: number) => void;
}

const HeatMapLayer: React.FC<HeatMapLayerProps> = ({ data, onExtremaChange }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || data.length === 0) return;

        const cfg = {
            radius: 0.125,
            maxOpacity: 0.7,
            minOpacity: 0.1,
            scaleRadius: true,
            useLocalExtrema: true,
            latField: 'lat',
            lngField: 'lng',
            valueField: 'count',
            onExtremaChange: (d: { min: number; max: number }) => {
                onExtremaChange?.(d.min, d.max);
            },
        };

        const heat = new HeatmapOverlay(cfg);
        const maxVal = Math.max(...data.map(d => d.count));
        heat.setData({ max: maxVal, data });
        map.addLayer(heat);

        return () => {
            map.removeLayer(heat);
        };
    }, [map, data, onExtremaChange]);

    return null;
};

/* -----------------------------------------------------------------------
   Helper: parse the polygon-string key → midpoint { lat, lng }
   Each key looks like: "[[-80.3, 27.6], [-80.4, 27.6], [-80.4, 27.7], [-80.3, 27.7], [-80.3, 27.6]]"
   pairs are [lng, lat].
----------------------------------------------------------------------- */
function parseStrikesToHeatPoints(strikes: Record<string, number>): HeatPoint[] {
    const points: HeatPoint[] = [];
    for (const [key, count] of Object.entries(strikes)) {
        try {
            const coords: [number, number][] = JSON.parse(key);
            // coords[0] = SW corner [lng, lat], coords[2] = NE corner [lng, lat]
            const midLng = (coords[0][0] + coords[2][0]) / 2;
            const midLat = (coords[0][1] + coords[2][1]) / 2;
            points.push({ lat: midLat, lng: midLng, count });
        } catch {
            // skip malformed keys
        }
    }
    return points;
}

/* -----------------------------------------------------------------------
   Inline gradient legend
----------------------------------------------------------------------- */
interface GradientLegendProps {
    min: number;
    max: number | null;
}

const GradientLegend: React.FC<GradientLegendProps> = ({ min, max }) => (
    <div style={{
        background: 'rgba(255,255,255,0.88)',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 1px 5px rgba(0,0,0,.2)',
    }}>
        <div style={{ marginBottom: 4, fontWeight: 600 }}>Strike Density</div>
        <div style={{
            width: 140,
            height: 12,
            borderRadius: 4,
            background: 'linear-gradient(to right, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000)',
            marginBottom: 4,
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{min}</span>
            <span>{max ?? '…'}</span>
        </div>
    </div>
);

/* ======================================================================
   Main component
====================================================================== */
const GisLightningAnalysis = () => {

    const [availableRegions, setAvailableRegions] = useState<string[]>([]);
    const [regionDatabases, setRegionDatabases] = useState<NotionChildDatabase[]>([]);
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1600);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 1600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /* ------------------------------------------------------------------
       Overview (GIS fire database)
    ------------------------------------------------------------------ */
    const [gisData, setGisData] = useState<GisFire[]>([])
    const [loadingOverview, setLoadingOverview] = useState(true)
    const [overviewViewMode, setOverviewViewMode] = useState<'chart' | 'list'>('chart')

    useEffect(() => {
        const fetchChildDatabases = async () => {
            try {
                const res = await fetch(
                    '/api/notion/page-blocks/1ff3634fc8d980c1913fec9c91defe86/',
                    { credentials: "same-origin" }
                )
                const data = await res.json()
                const childDbs: NotionChildDatabase[] = data.blocks
                    .filter((block: any) => block.type === 'child_database')
                    .map((block: any) => ({
                        id: block.id.replace(/-/g, ''),
                        title: block.child_database.title,
                    }))
                setRegionDatabases(childDbs)
                setAvailableRegions(childDbs.map(db => db.title))
                if (childDbs.length > 0) setSelectedRegion(childDbs[0].title)
            } catch (err) {
                console.error('Failed fetching region databases:', err)
            }
        }
        fetchChildDatabases()
    }, [])

    useEffect(() => {
        if (!selectedRegion) return
        const fetchRegionData = async () => {
            try {
                setLoadingOverview(true)
                const selectedDb = regionDatabases.find(db => db.title === selectedRegion)
                if (!selectedDb) return
                const res = await fetch(
                    `/api/notion/gis/fire-database/${selectedDb.id}`,
                    { credentials: "same-origin" }
                )
                const data: GisFire[] = await res.json()
                setGisData(data)
            } catch (err) {
                console.error('Failed fetching GIS data:', err)
            } finally {
                setLoadingOverview(false)
            }
        }
        fetchRegionData()
    }, [selectedRegion, regionDatabases])

    // Lightning ignitions
    const lightningCounts: Record<LightningStatus, number> = { Yes: 0, No: 0, Maybe: 0 }
    const lightningColorMap: Record<LightningStatus, string> = {
        Yes: '#29ABE2', No: '#0A6AA6', Maybe: '#1976D2',
    }
    gisData.forEach(row => { if (row.lightning_5day) lightningCounts[row.lightning_5day]++ })
    const lightningLabels = Object.keys(lightningCounts)
    const lightningSeries = Object.values(lightningCounts)
    const lightningTotal = lightningSeries.reduce((a, b) => a + b, 0)
    const donutTotalFontSize = isSmallScreen ? '12px' : '20px'
    const valueSpacing = isSmallScreen ? -20 : -25
    const nameSpacing = isSmallScreen ? 18 : 20

    // HRL buckets
    const hrlBuckets: Record<HrlBucket, number> = {
        None: 0, '0': 0, '10': 0, '20': 0, '30': 0,
        '40': 0, '50': 0, '60': 0, '70': 0, '80': 0, '90+': 0,
    }
    const HRL_COLOR_SCALE: Record<Exclude<HrlBucket, 'None'>, string> = {
        '0': '#9e0142', '10': '#d53e4f', '20': '#f46d43', '30': '#fdae61',
        '40': '#fee08b', '50': '#e6f598', '60': '#abdda4', '70': '#66c2a5',
        '80': '#3288bd', '90+': '#5e4fa2',
    }
    const getHrlColor = (bucket: HrlBucket) =>
        bucket === 'None' ? '#626262' : HRL_COLOR_SCALE[bucket as Exclude<HrlBucket, 'None'>]

    gisData.forEach(row => {
        const value = row.hrl_rating
        if (value === null || value === undefined) {
            hrlBuckets['None']++
        } else {
            const bucket = Math.floor(value / 10) * 10
            if (bucket >= 90) hrlBuckets['90+']++
            else hrlBuckets[bucket.toString() as HrlBucket]++
        }
    })
    const hrlLabels = Object.keys(hrlBuckets)
    const hrlSeries = Object.values(hrlBuckets)
    const hrlTotal = hrlSeries.reduce((a, b) => a + b, 0)

    const lightningChartOptions: ApexOptions = {
        chart: { type: 'donut' },
        labels: lightningLabels,
        colors: lightningLabels.map(l => lightningColorMap[l as LightningStatus]),
        legend: { position: 'bottom', fontSize: '12px', horizontalAlign: 'center', offsetY: 0, markers: { size: 5 } },
        plotOptions: {
            pie: {
                donut: {
                    size: '80%',
                    labels: {
                        show: true,
                        total: { show: true, label: 'Total', fontSize: donutTotalFontSize, fontWeight: 600, color: '#373d3f', formatter: () => lightningTotal.toString() },
                        name: { show: true, offsetY: nameSpacing },
                        value: { show: true, fontSize: donutTotalFontSize, color: '#373d3f', offsetY: valueSpacing },
                    },
                },
            },
        },
        tooltip: { y: { formatter: (v: number) => `${v} (${lightningTotal ? ((v / lightningTotal) * 100).toFixed(1) : '0'}%)` } },
        dataLabels: { enabled: false },
    }

    const hrlChartOptions: ApexOptions = {
        chart: { type: 'donut' },
        labels: hrlLabels,
        colors: hrlLabels.map(l => getHrlColor(l as HrlBucket)),
        legend: { position: 'bottom', fontSize: '12px', horizontalAlign: 'center', offsetY: 0, markers: { size: 5 } },
        plotOptions: {
            pie: {
                donut: {
                    size: '80%',
                    labels: {
                        show: true,
                        total: { show: true, label: 'Total', fontSize: donutTotalFontSize, fontWeight: 600, color: '#373d3f', formatter: () => hrlTotal.toString() },
                        name: { show: true, offsetY: nameSpacing },
                        value: { show: true, fontSize: donutTotalFontSize, color: '#373d3f', offsetY: valueSpacing },
                    },
                },
            },
        },
        tooltip: { y: { formatter: (v: number) => `${v} (${hrlTotal ? ((v / hrlTotal) * 100).toFixed(1) : '0'}%)` } },
        dataLabels: { enabled: false },
    }

    /* ------------------------------------------------------------------
       Archive Lightning Data
    ------------------------------------------------------------------ */
    const [archiveLightningData, setArchiveLightningData] = useState<GisArchiveLightning[]>([]);
    const [loadingArchive, setLoadingArchive] = useState(true);

    useEffect(() => {
        const fetchArchiveData = async () => {
            try {
                setLoadingArchive(true);
                const res = await fetch(
                    '/apidet/gis_lightning_archive/',
                    { credentials: "same-origin" }
                );
                const data: GisArchiveLightning[] = await res.json();
                setArchiveLightningData(data);
            } catch (err) {
                console.error('Failed fetching archive lightning data:', err);
            } finally {
                setLoadingArchive(false);
            }
        };
        fetchArchiveData();
    }, []);

    const getTotalStrikes = (strikes: Record<string, number>): number =>
        Object.values(strikes).reduce((a, b) => a + b, 0);

    const parseLocalDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return { year, month, day };
    };

    const archiveFlatData = archiveLightningData.map(item => ({
        date: item.date.split('T')[0],
        total: getTotalStrikes(item.strikes),
        region: item.region,
    }));

    /* ------------------------------------------------------------------
       Shared constants derived from data
    ------------------------------------------------------------------ */
    const AVAILABLE_YEARS = Array.from(
        new Set(archiveFlatData.map(item => parseLocalDate(item.date).year))
    ).sort((a, b) => b - a);
    const yearsForFilter = AVAILABLE_YEARS.length > 0 ? AVAILABLE_YEARS : [new Date().getFullYear()];


    const archiveRegions = Array.from(new Set(archiveLightningData.map(d => d.region))).sort();

    /* ------------------------------------------------------------------
       Map filter state
    ------------------------------------------------------------------ */
    const [mapSelectedRegion, setMapSelectedRegion] = useState<string>('All');
    const today = new Date();
    const [mapSelectedYears, setMapSelectedYears] = useState<number[]>([today.getFullYear()]);
    const [mapSelectedMonth, setMapSelectedMonth] = useState<number | null>(today.getMonth() + 1);
    const [mapSelectedDays, setMapSelectedDays] = useState<number[]>([today.getDate()]);
    const [heatmapMax, setHeatmapMax] = useState<number | null>(null);

    // Merge all matching archive groups into one heatmap point set
    const heatmapData: HeatPoint[] = React.useMemo(() => {
        const filtered = archiveLightningData.filter(item => {
            const { year, month, day } = parseLocalDate(item.date.split('T')[0]);
            const regionMatch = mapSelectedRegion === 'All' || item.region === mapSelectedRegion;
            const yearMatch = mapSelectedYears.length === 0 || mapSelectedYears.includes(year);
            const monthMatch = mapSelectedMonth === null || month === mapSelectedMonth;
            const dayMatch = mapSelectedDays.length === 0 || mapSelectedDays.includes(day);
            return regionMatch && yearMatch && monthMatch && dayMatch;
        });

        // Accumulate strike counts across all filtered groups
        const merged: Record<string, number> = {};
        for (const item of filtered) {
            for (const [key, count] of Object.entries(item.strikes)) {
                merged[key] = (merged[key] ?? 0) + count;
            }
        }

        return parseStrikesToHeatPoints(merged);
    }, [archiveLightningData, mapSelectedRegion, mapSelectedYears, mapSelectedMonth, mapSelectedDays]);

    const handleHeatmapExtrema = useCallback((_min: number, max: number) => {
        setHeatmapMax(max);
    }, []);

    /* ------------------------------------------------------------------
        Season Comparison filter state
    ------------------------------------------------------------------ */
    const [seasonSelectedRegion, setSeasonSelectedRegion] = useState<string>('All');

    const [dateRanges, setDateRanges] = useState<DateRange[]>([{
        id: "initial",
        label: "Season Range",
        startDate: today,
        endDate: today,
        selectedYear: today.getFullYear(),
    }]);

    // ── helpers ──────────────────────────────────────────────────────────

    /** Returns true if a range starts and ends within the same calendar month */
    const isSameMonth = (r: DateRange): boolean => {
        if (!r.startDate || !r.endDate) return false;
        return r.startDate.getMonth() === r.endDate.getMonth();
    };

    /** Filter archiveFlatData to rows that fall within a range's year + date window */

    const seasonFilteredFlatData = React.useMemo(() =>
        archiveFlatData.filter(item =>
            seasonSelectedRegion === 'All' || item.region === seasonSelectedRegion
        ),
        [archiveFlatData, seasonSelectedRegion]
    );

    const getDataForRange = (r: DateRange) => {
        return seasonFilteredFlatData.filter(item => {
            const { year, month, day } = parseLocalDate(item.date);
            if (year !== r.selectedYear) return false;
            if (!r.startDate || !r.endDate) return true;
            const sm = r.startDate.getMonth() + 1, sd = r.startDate.getDate();
            const em = r.endDate.getMonth() + 1, ed = r.endDate.getDate();
            return (month > sm || (month === sm && day >= sd)) &&
                (month < em || (month === em && day <= ed));
        });
    };

    /** Sum total strikes across all rows for a range */
    const sumRange = (r: DateRange): number =>
        getDataForRange(r).reduce((acc, item) => acc + item.total, 0);

    // ── determine chart mode ─────────────────────────────────────────────
    type SeasonMode =
        | 'multi-range-same-month'   // 2+ ranges, all within same month  → area, x = days
        | 'multi-range-diff-months'  // 2+ ranges, different months/years → bar,  x = range labels
        | 'single-day'               // 1 range, start === end             → bar,  x = date
        | 'single-same-month'        // 1 range, within one month          → area, x = days
        | 'single-multi-month';      // 1 range spanning months / no dates → area, x = months

    const seasonMode: SeasonMode = React.useMemo((): SeasonMode => {
        const active = dateRanges.filter(r => r.startDate && r.endDate);

        if (active.length > 1) {

            // 1️⃣ Each range must individually stay inside a month
            const allIndividuallySingleMonth = active.every(isSameMonth);
            if (!allIndividuallySingleMonth) {
                return 'multi-range-diff-months';
            }

            // 2️⃣ All ranges must share the SAME MONTH NUMBER
            const firstMonth = active[0].startDate!.getMonth();

            const allSameMonth = active.every(r =>
                r.startDate!.getMonth() === firstMonth
            );

            return allSameMonth
                ? 'multi-range-same-month'
                : 'multi-range-diff-months';
        }

        const r = active[0] ?? dateRanges[0];

        if (!r?.startDate || !r?.endDate)
            return 'single-multi-month';

        if (r.startDate.toDateString() === r.endDate.toDateString())
            return 'single-day';

        return isSameMonth(r)
            ? 'single-same-month'
            : 'single-multi-month';

    }, [dateRanges]);

    // ── ApexCharts option builders ───────────────────────────────────────
    const baseAreaOptions = (categories: (string | number)[], xTitle: string): ApexOptions => ({
        chart: { type: 'area', toolbar: { show: false } },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.05, stops: [0, 90, 100] } },
        dataLabels: { enabled: false },
        xaxis: { categories, title: { text: xTitle } },
        yaxis: { title: { text: 'Total Lightning Strikes' } },
        legend: { position: 'top', horizontalAlign: 'center' },
        tooltip: { y: { formatter: (v: number) => `${v} strikes` } },
    });

    const baseBarOptions = (categories: (string | number)[], xTitle: string): ApexOptions => ({
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 2, columnWidth: '30%' } },
        dataLabels: { enabled: false },
        xaxis: { categories, title: { text: xTitle } },
        yaxis: { title: { text: 'Total Lightning Strikes' } },
        legend: { position: 'top', horizontalAlign: 'center' },
        tooltip: { y: { formatter: (v: number) => `${v} strikes` } },
    });

    // ── build chart data per mode ────────────────────────────────────────
    const seasonChartProps = React.useMemo(() => {
        const activeRanges = dateRanges.filter(r => r.startDate && r.endDate);
        const singleRange = dateRanges[0];

        // ----------------------------------------------------------------
        // MULTI-RANGE, SAME MONTH → area, x = days, one series per range
        // ----------------------------------------------------------------
        if (seasonMode === 'multi-range-same-month') {
            const allDays = Array.from(new Set(
                activeRanges.flatMap(r =>
                    getDataForRange(r).map(item => parseLocalDate(item.date).day)
                )
            )).sort((a, b) => a - b);

            const series = activeRanges.map((r, i) => ({
                name: r.label || `Range ${i + 1}`,
                data: allDays.map(day => {
                    const found = getDataForRange(r).find(
                        item => parseLocalDate(item.date).day === day
                    );
                    return found ? found.total : 0;
                }),
            }));

            return {
                options: baseAreaOptions(allDays, 'Day of Month'),
                series,
                type: 'area' as const,
                needsScroll: allDays.length > 12,
                scrollWidth: `${allDays.length * 65}px`,
            };
        }

        // ----------------------------------------------------------------
        // MULTI-RANGE, DIFF MONTHS → bar, x = range labels, 1 bar per range
        // ----------------------------------------------------------------
        if (seasonMode === 'multi-range-diff-months') {
            const categories = activeRanges.map((r, i) => r.label || `Range ${i + 1}`);
            return {
                options: baseBarOptions(categories, 'Date Range'),
                series: [{ name: 'Total Strikes', data: activeRanges.map(r => sumRange(r)) }],
                type: 'bar' as const,
                needsScroll: categories.length > 12,
                scrollWidth: `${Math.max(categories.length * 80, 400)}px`,
            };
        }

        // ----------------------------------------------------------------
        // SINGLE DAY → bar, x = formatted date
        // ----------------------------------------------------------------
        if (seasonMode === 'single-day') {
            const dateLabel = singleRange?.startDate
                ? singleRange.startDate.toLocaleDateString('en-US', {
                    month: '2-digit', day: '2-digit', year: 'numeric',
                })
                : 'Selected Day';

            return {
                options: baseBarOptions([dateLabel], 'Date'),
                series: [{ name: 'Total Strikes', data: [sumRange(singleRange)] }],
                type: 'bar' as const,
                needsScroll: false,
                scrollWidth: '100%',
            };
        }

        // ----------------------------------------------------------------
        // SINGLE SAME MONTH → area, x = days
        // ----------------------------------------------------------------
        if (seasonMode === 'single-same-month') {
            const rangeData = getDataForRange(singleRange);
            const days = Array.from(
                new Set(rangeData.map(item => parseLocalDate(item.date).day))
            ).sort((a, b) => a - b);

            return {
                options: baseAreaOptions(days, 'Day of Month'),
                series: [{
                    name: singleRange.label || singleRange.selectedYear.toString(),
                    data: days.map(day => {
                        const found = rangeData.find(item => parseLocalDate(item.date).day === day);
                        return found ? found.total : 0;
                    }),
                }],
                type: 'area' as const,
                needsScroll: days.length > 12,
                scrollWidth: `${days.length * 65}px`,
            };
        }

        // ----------------------------------------------------------------
        // SINGLE MULTI-MONTH (or no dates) → area, x = months
        // ----------------------------------------------------------------
        const year = singleRange?.selectedYear ?? new Date().getFullYear();

        if (!singleRange?.startDate || !singleRange?.endDate) {
            // No filter: show every month with data for this year
            const monthTotals: Record<number, number> = {};
            seasonFilteredFlatData
                .filter(item => parseLocalDate(item.date).year === year)
                .forEach(item => {
                    const { month } = parseLocalDate(item.date);
                    monthTotals[month] = (monthTotals[month] ?? 0) + item.total;
                });
            const months = Object.keys(monthTotals).map(Number).sort((a, b) => a - b);
            const categories = months.map(m => new Date(0, m - 1).toLocaleString('en-US', { month: 'short' }));
            return {
                options: baseAreaOptions(categories, 'Month'),
                series: [{ name: year.toString(), data: months.map(m => monthTotals[m]) }],
                type: 'area' as const,
                needsScroll: false,
                scrollWidth: '100%',
            };
        }

        // Range spans multiple months
        const months: number[] = [];
        let cur = new Date(singleRange.startDate.getFullYear(), singleRange.startDate.getMonth(), 1);
        const endCap = new Date(singleRange.endDate.getFullYear(), singleRange.endDate.getMonth(), 1);
        while (cur <= endCap) {
            months.push(cur.getMonth() + 1);
            cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
        }
        const categories = months.map(m => new Date(0, m - 1).toLocaleString('en-US', { month: 'short' }));
        return {
            options: baseAreaOptions(categories, 'Month'),
            series: [{
                name: singleRange.label || year.toString(),
                data: months.map(month =>
                    seasonFilteredFlatData
                        .filter(item => {
                            const p = parseLocalDate(item.date);
                            return p.year === year && p.month === month;
                        })
                        .reduce((acc, item) => acc + item.total, 0)
                ),
            }],
            type: 'area' as const,
            needsScroll: categories.length > 12,
            scrollWidth: `${categories.length * 65}px`,
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seasonMode, dateRanges, seasonFilteredFlatData]);

    /* ------------------------------------------------------------------
       Regions Aggregation
    ------------------------------------------------------------------ */
    const formatCompactNumber = (value: number): string => {
        if (value >= 1_000_000) {
            return (value / 1_000_000)
                .toFixed(value >= 10_000_000 ? 0 : 1)
                .replace(/\.0$/, '') + 'M';
        }

        if (value >= 1_000) {
            return (value / 1_000)
                .toFixed(value >= 100_000 ? 0 : 1)
                .replace(/\.0$/, '') + 'K';
        }

        return value.toLocaleString();
    };

    const donutColors = [
        '#1E3A8A', // deep blue
        '#2563EB', // blue
        '#0EA5E9', // sky
        '#10B981', // emerald
        '#F59E0B', // amber
        '#EF4444', // red
        '#8B5CF6', // violet
        '#EC4899', // pink
    ];

    const [lightningRegionsViewMode, setLightningRegionsViewMode] = useState<'chart' | 'list'>('chart')
    const [regionSelectedYears, setRegionSelectedYears] = useState<number[]>([today.getFullYear()]);
    const [regionSelectedMonth, setRegionSelectedMonth] = useState<number | null>(today.getMonth() + 1);
    const [regionSelectedDays, setRegionSelectedDays] = useState<number[]>([today.getDate()]);
    const [regionSelectedRegion, setRegionSelectedRegion] = useState<string>('All');

    const regionFilteredData = archiveFlatData.filter(item => {
        const { year, month, day } = parseLocalDate(item.date);
        return (
            regionSelectedYears.includes(year) &&
            (regionSelectedRegion === 'All' || item.region === regionSelectedRegion) &&
            (regionSelectedMonth === null || month === regionSelectedMonth) &&
            (regionSelectedDays.length === 0 || regionSelectedDays.includes(day))
        );
    });

    const regionTotals: Record<string, number> = {};
    regionFilteredData.forEach(item => {
        regionTotals[item.region] = (regionTotals[item.region] ?? 0) + item.total;
    });
    const sortedRegions = Object.entries(regionTotals).sort((a, b) => b[1] - a[1]);
    const regionLabels = sortedRegions.map(([r]) => r);
    const regionSeries = sortedRegions.map(([, t]) => t);
    const regionTotalSum = regionSeries.reduce((a, b) => a + b, 0);

    const regionStrokeOptions: ApexOptions = {
        chart: { type: 'donut' },
        colors: donutColors, // ✅ palette added
        labels: regionLabels,
        legend: { position: 'bottom', fontSize: '12px', horizontalAlign: 'center', offsetY: 0, markers: { size: 5 } },
        plotOptions: {
            pie: {
                donut: {
                    size: '80%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: donutTotalFontSize,
                            fontWeight: 600,
                            color: '#373d3f',
                            formatter: () => formatCompactNumber(regionTotalSum), // ✅ compact format
                        },
                        name: { show: true, offsetY: nameSpacing },
                        value: {
                            show: true,
                            fontSize: donutTotalFontSize,
                            color: '#373d3f',
                            offsetY: valueSpacing,
                            formatter: (val: string) =>
                                formatCompactNumber(Number(val)), // ✅ compact slice value
                        },
                    },
                },
            },
        },
        tooltip: {
            y: {
                formatter: (v: number) =>
                    `${v.toLocaleString()} (${regionTotalSum ? ((v / regionTotalSum) * 100).toFixed(1) : '0'}%)`,
            },
        },
        dataLabels: { enabled: false },
    };

    const regionPercentOptions: ApexOptions = {
        ...regionStrokeOptions,
        tooltip: {
            y: {
                formatter: (v: number) =>
                    `${regionTotalSum ? ((v / regionTotalSum) * 100).toFixed(1) : '0'}% (${v.toLocaleString()} strikes)`,
            },
        },
    };

    /* ------------------------------------------------------------------
       County Aggregation
    ------------------------------------------------------------------ */
    const countyFilteredArchive = archiveLightningData.filter(item => {
        const { year, month, day } = parseLocalDate(item.date.split('T')[0]);
        return (
            regionSelectedYears.includes(year) &&
            (regionSelectedRegion === 'All' || item.region === regionSelectedRegion) &&
            (regionSelectedMonth === null || month === regionSelectedMonth) &&
            (regionSelectedDays.length === 0 || regionSelectedDays.includes(day))
        );
    });

    // Merge all county strike maps
    const countyTotals: Record<string, number> = {};

    countyFilteredArchive.forEach(item => {
        Object.entries(item.counties || {}).forEach(([county, count]) => {
            countyTotals[county] = (countyTotals[county] ?? 0) + count;
        });
    });

    // Sort descending
    const sortedCounties = Object.entries(countyTotals).sort((a, b) => b[1] - a[1]);

    // Take only top 5 counties
    const topCounties = sortedCounties.slice(0, 5);
    const othersTotal = sortedCounties.slice(5).reduce((sum, [, v]) => sum + v, 0);

    const countyLabels = topCounties.map(([c]) => c);
    const countySeries = topCounties.map(([, v]) => v);

    if (othersTotal > 0) {
        countyLabels.push("Others");
        countySeries.push(othersTotal);
    }

    const countyTotalSum = countySeries.reduce((a, b) => a + b, 0);

    const countyStrokeOptions: ApexOptions = {
        chart: { type: 'donut' },
        colors: donutColors, // ✅ palette added
        labels: countyLabels,
        legend: { position: 'bottom', fontSize: '12px', horizontalAlign: 'center', offsetY: 0, markers: { size: 5 } },
        plotOptions: {
            pie: {
                donut: {
                    size: '80%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: donutTotalFontSize,
                            fontWeight: 600,
                            color: '#373d3f',
                            formatter: () => formatCompactNumber(countyTotalSum), // ✅ compact format
                        },
                        name: { show: true, offsetY: nameSpacing },
                        value: {
                            show: true,
                            fontSize: donutTotalFontSize,
                            color: '#373d3f',
                            offsetY: valueSpacing,
                            formatter: (val: string) =>
                                formatCompactNumber(Number(val)), // ✅ compact slice value
                        },
                    },
                },
            },
        },
        tooltip: {
            y: {
                formatter: (v: number) =>
                    `${v.toLocaleString()} (${countyTotalSum ? ((v / countyTotalSum) * 100).toFixed(1) : '0'}%)`,
            },
        },
        dataLabels: { enabled: false },
    };

    /* ====================================================================
       RENDER
    ==================================================================== */
    return (
        <div className="customer-directory-body toa-body">
            <TeamHeader title="GIS" />

            <div className="customer-title-and-expand-buttons">
                <h1 className="cutomer-title">Lightning Analysis</h1>
            </div>

            <div className='toa-dash-container'>

                {/* ── Map Block ── */}
                <div className="toa-dash-block">

                    {loadingArchive ? (
                        <div className="toa-map-skeleton">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <div className="toa-map-wrapper" style={{ position: 'relative' }}>

                            {/* Filter buttons */}
                            <div className='gis-in-map-buttons'>
                                <FilterDropdown
                                    sideClass="detector-side-drop"
                                    initialSelected={{
                                        regions: [mapSelectedRegion === 'All' ? 'All Regions' : mapSelectedRegion],
                                    }}
                                    sections={[
                                        {
                                            key: 'regions',
                                            title: 'Region',
                                            options: ['All Regions', ...archiveRegions],
                                            type: 'radio',
                                        },
                                    ]}
                                    onChange={(filters) => {
                                        if (filters.regions?.length) {
                                            setMapSelectedRegion(
                                                filters.regions[0] === 'All Regions'
                                                    ? 'All'
                                                    : filters.regions[0]
                                            );
                                        }
                                    }}
                                />
                                <DateFilter
                                    dropdownClassName='map-calendar-dropdown'
                                    availableYears={yearsForFilter}
                                    maxCalendars={1}
                                    initialRanges={[{
                                        id: "map-range",
                                        label: "Date Range",
                                        startDate: today,
                                        endDate: today,
                                        selectedYear: new Date().getFullYear(),
                                    }]}
                                    onChange={(ranges: DateRange[]) => {
                                        const r = ranges[0];
                                        if (!r) return;

                                        // Year: always track which year tab is selected
                                        setMapSelectedYears([r.selectedYear]);

                                        // Month
                                        if (r.startDate && r.endDate && r.startDate.getMonth() === r.endDate.getMonth()) {
                                            setMapSelectedMonth(r.startDate.getMonth() + 1);
                                        } else {
                                            setMapSelectedMonth(null);
                                        }

                                        // Days
                                        if (r.startDate && r.endDate) {
                                            const days: number[] = [];
                                            let cur = new Date(r.startDate);
                                            while (cur <= r.endDate) {
                                                days.push(cur.getDate());
                                                cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
                                            }
                                            setMapSelectedDays(days);
                                        } else {
                                            setMapSelectedDays([]);
                                        }
                                    }}
                                />
                            </div>

                            <MapContainer
                                center={[27.8, -81.5]}
                                zoom={6}
                                minZoom={2}
                                style={{ width: '100%', height: '100%' }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                />
                                <ScaleControl position="bottomleft" metric={true} imperial={false} maxWidth={200} />
                                {heatmapData.length > 0 && (
                                    <HeatMapLayer data={heatmapData} onExtremaChange={handleHeatmapExtrema} />
                                )}
                            </MapContainer>

                            {/* Gradient legend overlay */}
                            <div style={{ position: 'absolute', bottom: 25, right: 5, zIndex: 999 }}>
                                <GradientLegend min={1} max={heatmapMax} />
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Overview Chart/List Block ── */}
                <div className="toa-dash-block">
                    <div className="request-title-and-times toa-location">
                        <div className='gis-chart-and-filter'>
                            <h1 className="toa-dash-block-performance-title">GIS Overview</h1>
                            <div className="marketing-chart-table-toggle">
                                <BiPieChartAlt2
                                    className={overviewViewMode === 'chart' ? 'active' : ''}
                                    onClick={() => setOverviewViewMode('chart')}
                                />
                                <BsList
                                    className={overviewViewMode === 'list' ? 'active' : ''}
                                    onClick={() => setOverviewViewMode('list')}
                                />
                            </div>
                        </div>
                        <div className="gis-region-select-wrapper">
                            <select
                                className="gis-region-select"
                                value={selectedRegion || ''}
                                onChange={(e) => setSelectedRegion(e.target.value)}
                            >
                                {availableRegions.map(region => (
                                    <option key={region} value={region}>{region}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {loadingOverview ? (
                        overviewViewMode === 'chart' ? (
                            <div className="toa-charts">
                                {['Lightning Ignitions', 'HRL Classification'].map(title => (
                                    <div key={title} className="toa-chart">
                                        <h1 className="marketing-donut-title">{title}</h1>
                                        <div className="marketing-donut" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <div className="marketing-skeleton" style={{ width: 200, height: 200, borderRadius: '50%' }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="toa-charts">
                                {['Lightning Ignitions', 'HRL Classification'].map(title => (
                                    <div key={title} className="toa-chart">
                                        <h1 className="marketing-donut-title">{title}</h1>
                                        <div className="gis-progress-container">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <div key={i} className="gis-progress-row">
                                                    <div className="marketing-skeleton" style={{ width: 60, height: 14 }} />
                                                    <div className="gis-progress-bar">
                                                        <div className="marketing-skeleton" style={{ width: '100%', height: 10 }} />
                                                    </div>
                                                    <div className="marketing-skeleton" style={{ width: 50, height: 14 }} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : overviewViewMode === 'chart' ? (
                        <div className="toa-charts">
                            <div className="toa-chart">
                                <h1 className="marketing-donut-title">Lightning Ignitions</h1>
                                <div className="marketing-donut">
                                    <ReactApexChart
                                        options={
                                            lightningTotal === 0
                                                ? {
                                                    ...lightningChartOptions,
                                                    labels: ['No Data'],
                                                    colors: ['#E5E7EB'], // light grey
                                                    tooltip: { enabled: false },
                                                    states: {
                                                        hover: {
                                                            filter: {
                                                                type: 'none', // ✅ disable hover effect
                                                            },
                                                        },
                                                        active: {
                                                            filter: {
                                                                type: 'none',
                                                            },
                                                        },
                                                    },
                                                }
                                                : lightningChartOptions
                                        }
                                        series={lightningTotal === 0 ? [1] : lightningSeries} // ✅ pass single value for "No Data"
                                        type="donut"
                                        height="100%"
                                    />
                                </div>
                            </div>
                            <div className="toa-chart">
                                <h1 className="marketing-donut-title">HRL Classification</h1>
                                <div className="marketing-donut">
                                    <ReactApexChart
                                        options={
                                            hrlTotal === 0
                                                ? {
                                                    ...hrlChartOptions,
                                                    labels: ['No Data'],
                                                    colors: ['#E5E7EB'], // light grey
                                                    tooltip: { enabled: false },
                                                    states: {
                                                        hover: {
                                                            filter: {
                                                                type: 'none', // ✅ disable hover effect
                                                            },
                                                        },
                                                        active: {
                                                            filter: {
                                                                type: 'none',
                                                            },
                                                        },
                                                    },
                                                }
                                                : hrlChartOptions
                                        }
                                        series={hrlTotal === 0 ? [1] : hrlSeries} // ✅ pass single value for "No Data"
                                        type="donut"
                                        height="100%" />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="toa-charts">
                            <div className="toa-chart">
                                <h1 className="marketing-donut-title">Lightning Ignitions</h1>
                                <div className="gis-progress-container">
                                    {lightningLabels.map((label, i) => {
                                        const value = lightningSeries[i]
                                        const percent = lightningTotal ? ((value / lightningTotal) * 100).toFixed(1) : '0'
                                        return (
                                            <div key={label} className="gis-progress-row">
                                                <div className="gis-progress-label">{label}</div>
                                                <div className="gis-progress-bar">
                                                    <div className="gis-progress-fill" style={{ width: `${percent}%`, background: lightningColorMap[label as LightningStatus] }} />
                                                </div>
                                                <div className="gis-progress-value">{value} ({percent}%)</div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                            <div className="toa-chart">
                                <h1 className="marketing-donut-title">HRL Classification</h1>
                                <div className="gis-progress-container">
                                    {hrlLabels
                                        .map((label, i) => ({ label, value: hrlSeries[i] }))
                                        .filter(item => item.value > 0)
                                        .sort((a, b) => {
                                            if (a.label === 'None') return 1
                                            if (b.label === 'None') return -1
                                            return parseInt(a.label) - parseInt(b.label)
                                        })
                                        .map(({ label, value }) => {
                                            const percent = hrlTotal ? ((value / hrlTotal) * 100).toFixed(1) : '0'
                                            return (
                                                <div key={label} className="gis-progress-row">
                                                    <div className="gis-progress-label">{label}</div>
                                                    <div className="gis-progress-bar">
                                                        <div className="gis-progress-fill" style={{ width: `${percent}%`, background: getHrlColor(label as HrlBucket) }} />
                                                    </div>
                                                    <div className="gis-progress-value">{value} ({percent}%)</div>
                                                </div>
                                            )
                                        })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Season Comparison Block ── */}
                <div className="toa-dash-block">
                    <div className="request-title-and-times toa-location">
                        <h1 className="toa-dash-block-performance-title">Season Comparison</h1>

                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <FilterDropdown
                                dropdownClassName='top-calendar-dropdown'
                                sideClass="detector-side-drop"
                                initialSelected={{
                                    regions: [seasonSelectedRegion === 'All' ? 'All Regions' : seasonSelectedRegion],
                                }}
                                sections={[
                                    {
                                        key: 'regions',
                                        title: 'Region',
                                        options: ['All Regions', ...archiveRegions],
                                        type: 'radio',
                                    },
                                ]}
                                onChange={(filters) => {
                                    if (filters.regions?.length) {
                                        setSeasonSelectedRegion(
                                            filters.regions[0] === 'All Regions' ? 'All' : filters.regions[0]
                                        );
                                    }
                                }}
                            />
                            <DateFilter
                                dropdownClassName='top-calendar-dropdown'
                                availableYears={yearsForFilter}
                                initialRanges={[{
                                    id: "initial",
                                    label: "Season Range",
                                    startDate: today,
                                    endDate: today,
                                    selectedYear: new Date().getFullYear(),
                                }]}
                                onChange={(ranges: DateRange[]) => {
                                    setDateRanges(ranges);
                                }}
                            />
                        </div>
                    </div>

                    <div className="toa-chart efficiency-legend-fix">
                        {loadingArchive ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <LoadingSpinner />
                            </div>
                        ) : seasonChartProps.series.some(s =>
                            s.data.some(value => value > 0)
                        ) ? (
                            <div className="efficiency-chart-scroll-wrapper gis-light-ext">
                                <div
                                    className="efficiency-chart-inner"
                                    style={{ width: seasonChartProps.needsScroll ? seasonChartProps.scrollWidth : '100%' }}
                                >
                                    <ReactApexChart
                                        key={`${seasonMode}-${seasonChartProps.series.map(s => s.name).join('-')}`}
                                        className="season-area-chart"
                                        options={seasonChartProps.options}
                                        series={seasonChartProps.series}
                                        type={seasonChartProps.type}
                                        height="100%"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="no-data-message">No data for the selected time period.</div>
                        )}
                    </div>
                </div>

                {/* ── Regions Chart/List Block ── */}
                <div className="toa-dash-block marketing-chart-block">
                    <div className="request-title-and-times toa-location">
                        <div className='gis-chart-and-filter'>
                            <h1 className='toa-dash-block-performance-title'>Regional Lightning</h1>
                            <div className="marketing-chart-table-toggle">
                                <BiPieChartAlt2
                                    className={lightningRegionsViewMode === 'chart' ? 'active' : ''}
                                    onClick={() => setLightningRegionsViewMode('chart')}
                                />
                                <BsList
                                    className={lightningRegionsViewMode === 'list' ? 'active' : ''}
                                    onClick={() => setLightningRegionsViewMode('list')}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <FilterDropdown
                                dropdownClassName='left-calendar-dropdown'
                                sideClass="detector-side-drop"
                                initialSelected={{
                                    regions: [regionSelectedRegion === 'All' ? 'All Regions' : regionSelectedRegion],
                                }}
                                sections={[
                                    {
                                        key: 'regions',
                                        title: 'Region',
                                        options: ['All Regions', ...archiveRegions],
                                        type: 'radio',
                                    },
                                ]}
                                onChange={(filters) => {
                                    if (filters.regions?.length) {
                                        setRegionSelectedRegion(
                                            filters.regions[0] === 'All Regions' ? 'All' : filters.regions[0]
                                        );
                                    }
                                }}
                            />
                            <DateFilter
                                dropdownClassName='left-calendar-dropdown'
                                availableYears={yearsForFilter}
                                maxCalendars={1}
                                initialRanges={[{
                                    id: "regions-range",
                                    label: "Date Range",
                                    startDate: today,
                                    endDate: today,
                                    selectedYear: today.getFullYear(),
                                }]}
                                onChange={(ranges: DateRange[]) => {
                                    const r = ranges[0];
                                    if (!r) return;
                                    setRegionSelectedYears([r.selectedYear]);
                                    if (r.startDate && r.endDate && r.startDate.getMonth() === r.endDate.getMonth()) {
                                        setRegionSelectedMonth(r.startDate.getMonth() + 1);
                                    } else {
                                        setRegionSelectedMonth(null);
                                    }
                                    if (r.startDate && r.endDate) {
                                        const days: number[] = [];
                                        let cur = new Date(r.startDate);
                                        while (cur <= r.endDate) {
                                            days.push(cur.getDate());
                                            cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
                                        }
                                        setRegionSelectedDays(days);
                                    } else {
                                        setRegionSelectedDays([]);
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {loadingArchive ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <LoadingSpinner />
                        </div>
                    ) : lightningRegionsViewMode === 'chart' ? (
                        <div className='toa-charts'>
                            <div className='toa-chart'>
                                <h1 className='marketing-donut-title'>Strikes by Region</h1>
                                <div className='marketing-donut'>
                                    <ReactApexChart
                                        key={`region-${regionTotalSum === 0 ? 'empty' : 'data'}`}
                                        options={
                                            regionTotalSum === 0
                                                ? {
                                                    ...regionStrokeOptions,
                                                    labels: ['No Data'],
                                                    colors: ['#E5E7EB'], // light grey
                                                    tooltip: { enabled: false },
                                                    states: {
                                                        hover: {
                                                            filter: {
                                                                type: 'none', // ✅ disable hover effect
                                                            },
                                                        },
                                                        active: {
                                                            filter: {
                                                                type: 'none',
                                                            },
                                                        },
                                                    },
                                                }
                                                : regionStrokeOptions
                                        }
                                        series={regionTotalSum === 0 ? [1] : regionSeries}
                                        type="donut"
                                        height="100%"
                                    />
                                </div>
                            </div>
                            <div className='toa-chart'>
                                <h1 className='marketing-donut-title'>Strikes by County</h1>
                                <div className='marketing-donut'>
                                    <ReactApexChart
                                        key={`county-${countyTotalSum === 0 ? 'empty' : 'data'}`}
                                        options={
                                            countyTotalSum === 0
                                                ? {
                                                    ...countyStrokeOptions,
                                                    labels: ['No Data'],
                                                    colors: ['#E5E7EB'],
                                                    tooltip: { enabled: false },
                                                    states: {
                                                        hover: {
                                                            filter: {
                                                                type: 'none', // ✅ disable hover effect
                                                            },
                                                        },
                                                        active: {
                                                            filter: {
                                                                type: 'none',
                                                            },
                                                        },
                                                    },
                                                }
                                                : countyStrokeOptions
                                        }
                                        series={countyTotalSum === 0 ? [1] : countySeries}
                                        type="donut"
                                        height="100%"
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className='marketing-lists'>
                            {/* ── Strikes by Region ── */}
                            <div className='marketing-list'>
                                <h1 className='marketing-list-title'>Strikes by Region</h1>
                                <ul className='marketing-stats-list'>
                                    {sortedRegions.map(([region, total]) => {
                                        const percent = regionTotalSum
                                            ? ((total / regionTotalSum) * 100).toFixed(1)
                                            : '0';

                                        return (
                                            <li key={region}>
                                                <span className='marketing-label'>{region}</span>
                                                <span className='marketing-value'>
                                                    {total.toLocaleString()} ({percent}%)
                                                </span>
                                            </li>
                                        );
                                    })}
                                    <li className='marketing-total'>
                                        <span className='marketing-label'>Total</span>
                                        <span className='marketing-value'>
                                            {regionTotalSum.toLocaleString()}
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            {/* ── Strikes by County ── */}
                            <div className='marketing-list'>
                                <h1 className='marketing-list-title'>Strikes by County</h1>
                                <ul className='marketing-stats-list'>
                                    {sortedCounties.map(([county, total]) => {
                                        const percent = countyTotalSum
                                            ? ((total / countyTotalSum) * 100).toFixed(1)
                                            : '0';

                                        return (
                                            <li key={county}>
                                                <span className='marketing-label'>{county}</span>
                                                <span className='marketing-value'>
                                                    {total.toLocaleString()} ({percent}%)
                                                </span>
                                            </li>
                                        );
                                    })}
                                    <li className='marketing-total'>
                                        <span className='marketing-label'>Total</span>
                                        <span className='marketing-value'>
                                            {countyTotalSum.toLocaleString()}
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    )
}

export default GisLightningAnalysis